import React, { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import API from "../services/api"
import BottomNav from "../components/BottomNav"
import { getUser } from '../utils/storage'
import { getTeamLogo, getAssetURL } from "../configs/assets"
import { parseMatchTime } from "../utils/dateUtils"

const MyTeams = () => {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const [view, setView] = useState("MATCHES") // MATCHES or DETAILS
    const [selectedMatch, setSelectedMatch] = useState(null)
    const [matches, setMatches] = useState([])
    const [loading, setLoading] = useState(true)

    // Details View States
    const [activeTab, setActiveTab] = useState("teams")
    const [myTeams, setMyTeams] = useState([])
    const [myContests, setMyContests] = useState([])
    const [myChallenges, setMyChallenges] = useState([])
    const [selectedTeamForModal, setSelectedTeamForModal] = useState(null)

    // Player Edit Modal States
    const [showPlayerEditModal, setShowPlayerEditModal] = useState(false)
    const [editingEntry, setEditingEntry] = useState(null)
    const [availablePlayers, setAvailablePlayers] = useState([])
    const [editRoleFilter, setEditRoleFilter] = useState("EVERYONE")
    const [updatingPlayer, setUpdatingPlayer] = useState(false)
    const [editPrediction, setEditPrediction] = useState(0)

    useEffect(() => {
        fetchMyMatches()
    }, [])

    // Handle deep linking / back button
    useEffect(() => {
        const matchId = searchParams.get("matchId")
        if (matchId) {
            const match = matches.find(m => String(m.match_id) === String(matchId))
            if (match) {
                setSelectedMatch(match)
                setView("DETAILS")
                fetchMatchDetails(matchId)
            } else {
                // If the user hasn't made any teams, fetch independently
                fetchIndependentMatch(matchId)
                fetchMatchDetails(matchId)
            }
        } else {
            setView("MATCHES")
            setSelectedMatch(null)
        }
    }, [searchParams, matches])

    // 🔴 AUTO-REFRESH LIVE SCORES every 10 seconds
    useEffect(() => {
        if (!selectedMatch || view !== "DETAILS") return;
        const refreshScore = async () => {
            try {
                const res = await API.get(`/matches/${selectedMatch.match_id}`);
                const data = Array.isArray(res.data) ? res.data[0] : (res.data?.data?.[0] || res.data);
                if (data) {
                    setSelectedMatch(prev => ({
                        ...prev,
                        team1_runs: data.team1_runs ?? prev.team1_runs ?? 0,
                        team1_wickets: data.team1_wickets ?? prev.team1_wickets ?? 0,
                        team1_overs: data.team1_overs ?? prev.team1_overs ?? '0',
                        team2_runs: data.team2_runs ?? prev.team2_runs ?? 0,
                        team2_wickets: data.team2_wickets ?? prev.team2_wickets ?? 0,
                        team2_overs: data.team2_overs ?? prev.team2_overs ?? '0',
                        status: data.status ?? prev.status
                    }));
                }
            } catch (err) { /* silent */ }
        };
        const interval = setInterval(refreshScore, 10000);
        return () => clearInterval(interval);
    }, [selectedMatch?.match_id, view])

    const fetchIndependentMatch = async (mId) => {
        try {
            setLoading(true)
            const res = await API.get(`/matches/${mId}`)
            const data = res.data
            if (data && data.length > 0) {
                setSelectedMatch(data[0])
                setView("DETAILS")
            }
        } catch (err) {
            console.log(err)
        } finally {
            setLoading(false)
        }
    }

    const fetchMyMatches = async () => {
        try {
            setLoading(true)
            const user = getUser()
            const userId = user.id || 1;

            // Fetch user's joined matches (Now optimized to return complete objects)
            const res = await API.get(`/team/my-matches?userId=${userId}`)
            setMatches(res.data)
        } catch (error) {
            console.error("Fetch matches error:", error)
        } finally {
            setLoading(false)
        }
    }

    const handleMatchClick = async (match) => {
        setSearchParams({ matchId: match.match_id })
    }

    const fetchMatchDetails = async (matchId) => {
        try {
            setLoading(true)
            const user = getUser()
            const id = user.id || 1

            const [teamsRes, contestsRes, challengesRes] = await Promise.all([
                API.get(`/team/my-teams?userId=${id}&matchId=${matchId}`),
                API.get(`/contests/my-contests?userId=${id}&matchId=${matchId}`),
                API.get(`/challenges/my/${id}?matchId=${matchId}`)
            ])

            setMyTeams(teamsRes.data.teams || [])
            setMyContests(contestsRes.data || [])
            setMyChallenges(challengesRes.data?.entries || [])
        } catch (error) {
            console.error("Match detail fetch error:", error)
        } finally {
            setLoading(false)
        }
    }

    const handleOpenEditModal = async (entry, e) => {
        e.stopPropagation();
        setEditingEntry(entry);
        setEditPrediction(Number(entry.prediction) || 0);
        try {
            setLoading(true);
            const res = await API.get(`/players/${entry.match_id}`);
            setAvailablePlayers(res.data || []);
            setShowPlayerEditModal(true);
        } catch (error) {
            console.error("Error fetching players for edit:", error);
            alert("Could not load players list.");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePlayer = async (newPlayerId) => {
        if (!editingEntry) return;
        setUpdatingPlayer(true);
        try {
            await API.patch(`/challenges/entry/${editingEntry.id}`, { 
                player_id: newPlayerId,
                prediction: editPrediction 
            });
            setShowPlayerEditModal(false);
            // Refresh challenges
            fetchMatchDetails(selectedMatch.match_id);
            alert("Player updated successfully!");
        } catch (error) {
            console.error("Update player error:", error);
            alert(error.response?.data?.error || "Failed to update player.");
        } finally {
            setUpdatingPlayer(false);
        }
    };

    const calculateTeamPoints = (players) => {
        if (!players || !Array.isArray(players)) return "0.0";
        return players.reduce((sum, p) => {
            const pts = parseFloat(p.points || 0)
            const multiplier = p.is_captain ? 2 : (p.is_vice_captain ? 1.5 : 1)
            return sum + (pts * multiplier)
        }, 0).toFixed(1)
    }

    // --- RENDER MATCH LIST ---
    if (view === "MATCHES") {
        return (
            <div className="bg-theme-bg min-h-screen text-theme-text pb-24">
                <div className="p-6 pt-10 text-center">
                    <h1 className="text-2xl font-bold tracking-tight text-theme-text mb-1 uppercase">My Gameplay</h1>
                    <p className="text-[10px] text-theme-text-sec font-medium uppercase tracking-wider mb-8">Matches you've participated in</p>

                    {loading ? (
                        <div className="flex justify-center py-20">
                            <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin shadow-[0_0_15px_rgba(220,38,38,0.3)]"></div>
                        </div>
                    ) : matches.length === 0 ? (
                        <div className="text-center py-32 bg-theme-card rounded-[40px] border border-theme-border shadow-theme-sm">
                            <div className="text-5xl mb-6">🏏</div>
                            <p className="text-theme-text-sec font-bold text-sm mb-6 uppercase tracking-widest">No Matches Found</p>
                            <button onClick={() => navigate("/home")} className="bg-red-600 px-10 py-4 rounded-full font-black text-xs uppercase tracking-widest shadow-2xl">Start Playing</button>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {matches.map(m => {
                                const colors = [
                                    { border: "border-orange-500/30", shadow: "shadow-orange-500/10", glow: "bg-orange-500/20", text: "text-orange-500" },
                                    { border: "border-blue-500/30", shadow: "shadow-blue-500/10", glow: "bg-blue-500/20", text: "text-blue-500" },
                                    { border: "border-pink-500/30", shadow: "shadow-pink-500/10", glow: "bg-pink-500/20", text: "text-pink-500" },
                                    { border: "border-purple-500/30", shadow: "shadow-purple-500/10", glow: "bg-purple-500/20", text: "text-purple-500" },
                                    { border: "border-emerald-500/30", shadow: "shadow-emerald-500/10", glow: "bg-emerald-500/20", text: "text-emerald-500" },
                                    { border: "border-yellow-500/40", shadow: "shadow-yellow-500/10", glow: "bg-yellow-501/20", text: "text-yellow-500" },
                                ];
                                const colorIndex = (String(m.match_id || "").length + (m.id || 0)) % colors.length;
                                const theme = colors[colorIndex];
                                const now = new Date()
                                const startTime = parseMatchTime(m)
                                const fiveHoursAfterStart = new Date(startTime.getTime() + 5 * 60 * 60 * 1000)
                                const matchStatus = m.status || (
                                    startTime > now ? 'upcoming' :
                                        now < fiveHoursAfterStart ? 'live' : 'completed'
                                )
                                const isUpcoming = matchStatus === 'upcoming'
                                const isLive = matchStatus === 'live'
                                const isCompleted = matchStatus === 'completed'

                                return (
                                    <div
                                        key={m.match_id}
                                        onClick={() => handleMatchClick(m)}
                                        className="relative bg-theme-card rounded-theme p-5 flex flex-col transition-all duration-300 active:scale-95 shadow-theme-sm border border-theme-border group overflow-hidden min-h-[200px] cursor-pointer"
                                    >
                                        <div className="flex justify-between items-start mb-5 relative z-10">
                                            <span className="bg-theme-bg text-[8px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-theme-border text-theme-text-sec">
                                                {(m.my_teams_count || 0) + (m.my_challenges_count || 0)} Participating
                                            </span>
                                            <div className="text-right">
                                                <div className="text-[10px] font-black italic uppercase tracking-[0.2em] flex items-center justify-end gap-2 mb-1.5">
                                                    {isCompleted ? (
                                                        <span className="text-theme-text-sec font-bold uppercase tracking-widest">✅ DONE</span>
                                                    ) : isLive ? (
                                                        <><span className="w-2 h-2 bg-red-600 rounded-full animate-pulse shadow-[0_0_10px_rgba(220,38,38,0.8)]"></span> <span className="text-red-500">LIVE</span></>
                                                    ) : <span className="text-theme-text-sec font-bold uppercase tracking-widest">UPCOMING</span>}
                                                </div>
                                                <div className="text-[9px] font-bold text-theme-text-sec/80 uppercase tracking-widest bg-theme-bg/60 px-3 py-1 rounded-full border border-theme-border/20">
                                                    {parseMatchTime(m).toLocaleString('en-IN', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        hour12: true
                                                    })}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between gap-4 relative z-10 px-1 mt-1">
                                            {/* Team 1 */}
                                            <div className="flex flex-col items-center gap-2 flex-1">
                                                <div className="w-12 h-12 rounded-full overflow-hidden bg-theme-card border border-theme-border shadow-sm flex items-center justify-center p-1.5 transition-all duration-300 group-hover:scale-110">
                                                    <img src={getTeamLogo(m.team1, m.team1_logo)} className="w-full h-full object-contain" alt={m.team1} />
                                                </div>
                                                <span className="text-[10px] font-bold text-theme-text uppercase tracking-tight">{m.team1_short || m.team1?.substring(0, 3)}</span>
                                            </div>

                                            {/* ⚔️ VS BADGE */}
                                            <div className="w-7 h-7 rounded-full bg-theme-bg border border-theme-border flex items-center justify-center">
                                                <span className="text-[8px] font-bold text-theme-text-sec italic">VS</span>
                                            </div>

                                            {/* Team 2 */}
                                            <div className="flex flex-col items-center gap-2 flex-1">
                                                <div className="w-12 h-12 rounded-full overflow-hidden bg-theme-card border border-theme-border shadow-sm flex items-center justify-center p-1.5 transition-all duration-300 group-hover:scale-110">
                                                    <img src={getTeamLogo(m.team2, m.team2_logo)} className="w-full h-full object-contain" alt={m.team2} />
                                                </div>
                                                <span className="text-[10px] font-bold text-theme-text uppercase tracking-tight">{m.team2_short || m.team2?.substring(0, 3)}</span>
                                            </div>
                                        </div>

                                        <div className="self-stretch mt-auto bg-theme-bg -mx-5 -mb-5 py-3 flex justify-center items-center border-t border-theme-border relative z-10 group-hover:bg-theme-bg/80 transition-all rounded-b-theme">
                                            <span className="text-[9px] text-theme-text-sec font-bold uppercase tracking-widest">
                                                {m.status === 'completed' ? 'Match Finished' : (m.short_title || 'T20 Match')}
                                            </span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
                <BottomNav activeTab="my-teams" />
            </div>
        )
    }

    if (view === "DETAILS" && !selectedMatch) {
        return (
            <div className="bg-theme-card min-h-screen text-theme-text flex justify-center items-center">
                <div className="flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-4 shadow-[0_0_15px_rgba(220,38,38,0.3)]"></div>
                    <p className="text-[10px] text-theme-text-sec font-bold uppercase tracking-widest animate-pulse">Loading Match Center...</p>
                </div>
            </div>
        )
    }

    // --- RENDER MATCH DETAILS ---
    return (
        <div className="bg-theme-bg min-h-screen text-theme-text pb-24">
            {/* 🏆 STICKY LIVE SCORE HEADER */}
            <div className="sticky top-0 z-[60] bg-theme-card border-b border-theme-border shadow-theme-sm">
                <div className="flex items-center p-3 px-4">
                    <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center bg-theme-bg rounded-lg border border-theme-border text-lg font-bold">←</button>
                    <div className="flex-1 text-center font-bold text-[10px] uppercase tracking-wider text-theme-text-sec">Match Center</div>
                    <div className="w-8 h-8"></div>
                </div>

                <div className="px-4 pb-2">
                    <div className="flex justify-between items-center bg-theme-bg rounded-xl p-3.5 border border-theme-border relative">
                        <div className="flex flex-col items-start min-w-0">
                            <span className="text-[9px] font-bold text-theme-text-sec uppercase mb-1 truncate w-full">{selectedMatch.team1}</span>
                            <h2 className="text-lg font-bold">
                                {selectedMatch.team1_runs ?? 0}/{selectedMatch.team1_wickets ?? 0}
                                <span className="text-[10px] text-theme-text-sec ml-2 font-normal">({selectedMatch.team1_overs ?? '0'})</span>
                            </h2>
                        </div>
                        <div className="h-8 w-px bg-theme-border mx-2"></div>
                        <div className="flex flex-col items-end min-w-0">
                            <span className="text-[9px] font-bold text-theme-text-sec uppercase mb-1 truncate w-full text-right">{selectedMatch.team2}</span>
                            <h2 className="text-lg font-bold">
                                {selectedMatch.team2_runs ?? 0}/{selectedMatch.team2_wickets ?? 0}
                                <span className="text-[10px] text-theme-text-sec ml-2 font-normal">({selectedMatch.team2_overs ?? '0'})</span>
                            </h2>
                        </div>
                    </div>
                </div>

                <div className="flex mt-1 border-t border-theme-border">
                    {["teams", "contests", "player_contests"].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`flex-1 py-3 text-[10px] font-bold uppercase tracking-wider transition-all relative ${activeTab === tab ? 'text-accent' : 'text-theme-text-sec'}`}
                        >
                            {tab.replace("_", " ")}
                            {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent"></div>}
                        </button>
                    ))}
                </div>
            </div>

            {/* 📝 TAB CONTENT */}
            <div className="p-4 pt-8">
                {activeTab === "teams" && (
                    <div className="space-y-8">
                        {(() => {
                            const isStarted = selectedMatch && (parseMatchTime(selectedMatch) <= new Date() || selectedMatch.status?.toLowerCase() === "live" || selectedMatch.status?.toLowerCase() === "completed");
                            if (isStarted) return null;
                            return (
                                <button
                                    onClick={() => navigate(`/create-team/${selectedMatch.match_id}`)}
                                    className="w-full bg-accent/10 border border-dashed border-accent/30 rounded-xl py-4 flex items-center justify-center gap-3 active:scale-[0.98] transition-all group mb-2"
                                >
                                    <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-white text-lg font-bold shadow-lg shadow-accent/20 group-hover:scale-110 transition-transform">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                    </div>
                                    <span className="text-[10px] font-black uppercase tracking-[0.15em] text-accent">Create New Team</span>
                                </button>
                            );
                        })()}
                        {myTeams.map((team, idx) => {
                            const playersList = team.players || [];
                            const captain = playersList.find(p => p.is_captain)
                            const viceCaptain = playersList.find(p => p.is_vice_captain)
                            const hasNotPlaying = playersList.some(p => p.is_playing === false || String(p.is_playing) === "0")

                            return (
                                <div key={team.team_id} className="relative group">
                                    <div className="absolute top-14 right-6 text-right z-20">
                                        <span className="text-[9px] font-bold text-theme-text-sec uppercase tracking-wider block mb-0">Points</span>
                                        <span className="text-xl font-bold text-theme-text">{calculateTeamPoints(playersList)}</span>
                                    </div>
                                    <div
                                        onClick={() => setSelectedTeamForModal(team)}
                                        className="bg-theme-card border border-theme-border rounded-xl overflow-hidden cursor-pointer shadow-theme-sm relative active:scale-95 transition-all"
                                    >
                                        {/* ✏️ EDIT BUTTON (Only if match not started) */}
                                        {(() => {
                                            const now = new Date();
                                            const matchStart = parseMatchTime(selectedMatch);
                                            // Allow editing up until 1 minute after start_time as a buffer, OR if status is upcoming
                                            const isStarted = (selectedMatch.status?.toLowerCase() === "live" || selectedMatch.status?.toLowerCase() === "completed") || (now > new Date(matchStart.getTime() + 60000));
                                            
                                            if (isStarted) return null;
                                            return (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const mId = selectedMatch?.match_id || searchParams.get("matchId");
                                                        const tId = team.id || team.team_id;
                                                        if (mId && tId) {
                                                            navigate(`/create-team/${mId}?editTeamId=${tId}`);
                                                        } else {
                                                            alert("Could not identify team or match for editing.");
                                                        }
                                                    }}
                                                    className="absolute top-4 right-4 w-9 h-9 bg-accent/10 rounded-lg border border-accent/20 flex items-center justify-center z-40 active:scale-90 transition-all cursor-pointer pointer-events-auto"
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-accent">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                    </svg>
                                                </button>
                                            );
                                        })()}
                                        <div className="p-5 flex items-center justify-between">
                                            <div className="flex-1">
                                                <h3 className="text-base font-bold uppercase tracking-tight text-theme-text flex items-center">
                                                    Your Team {idx + 1}
                                                    {selectedMatch?.lineup_announced && hasNotPlaying && (
                                                        <span className="w-2 h-2 bg-red-500 rounded-full ml-3 animate-pulse"></span>
                                                    )}
                                                </h3>

                                                <div className="flex items-center gap-4 mt-6">
                                                    {/* 👑 Captain Badge */}
                                                    <div className="flex items-center gap-2.5 p-1 px-3 bg-gradient-to-r from-yellow-500/10 to-transparent rounded-full border border-yellow-500/20 shadow-sm backdrop-blur-sm">
                                                        <div className="relative">
                                                            <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.3)]">
                                                                <img src={getAssetURL(captain?.image) || "/player_placeholder.png"} className="w-full h-full object-cover" alt="" />
                                                            </div>
                                                            <div className="absolute -top-1 -right-1 bg-yellow-500 text-black text-[7px] w-4 h-4 rounded-full flex items-center justify-center font-black border border-black z-10">C</div>
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className="text-[7px] font-black text-yellow-500 uppercase tracking-widest leading-none mb-0.5">Captain</span>
                                                            <span className="text-[10px] font-bold text-theme-text tracking-tight truncate max-w-[70px] leading-none">{captain?.player_name.split(" ")[0]}</span>
                                                        </div>
                                                    </div>

                                                    {/* 🎖️ Vice-Captain Badge */}
                                                    <div className="flex items-center gap-2.5 p-1 px-3 bg-gradient-to-r from-blue-500/10 to-transparent rounded-full border border-blue-500/20 shadow-sm backdrop-blur-sm">
                                                        <div className="relative">
                                                            <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.3)]">
                                                                <img src={getAssetURL(viceCaptain?.image) || "/player_placeholder.png"} className="w-full h-full object-cover" alt="" />
                                                            </div>
                                                            <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-[7px] w-4 h-4 rounded-full flex items-center justify-center font-black border border-black z-10">VC</div>
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className="text-[7px] font-black text-blue-500 uppercase tracking-widest leading-none mb-0.5">V-Captain</span>
                                                            <span className="text-[10px] font-bold text-theme-text tracking-tight truncate max-w-[70px] leading-none">{viceCaptain?.player_name.split(" ")[0]}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Join Contest Button at bottom of card (Only if match not started) */}
                                        {(() => {
                                            const isStarted = selectedMatch && (parseMatchTime(selectedMatch) <= new Date() || selectedMatch.status?.toLowerCase() === "live" || selectedMatch.status?.toLowerCase() === "completed");
                                            if (isStarted) return null;
                                            return (
                                                <div className="border-t border-theme-border p-3 flex justify-center">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/match/${selectedMatch.match_id}?teamId=${team.team_id || team.id}`);
                                                        }}
                                                        className="bg-accent px-6 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-wider text-white shadow-sm active:scale-95 transition-all"
                                                    >
                                                        Join Contest
                                                    </button>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}

                {activeTab === "contests" && (
                    <div className="space-y-5">
                        {myContests.length === 0 ? (
                            <div className="text-center py-24 bg-theme-card rounded-xl border border-theme-border">
                                <div className="text-4xl mb-4 opacity-30">🏆</div>
                                <p className="text-theme-text-sec font-bold text-[10px] uppercase tracking-widest">No contests joined for this match</p>
                            </div>
                        ) : (
                            myContests.map(c => (
                                <div
                                    key={c.id}
                                    onClick={() => navigate(`/leaderboard/${c.id}`)}
                                    className="relative group cursor-pointer"
                                >
                                    <div className="relative bg-theme-card border border-theme-border rounded-xl p-5 hover:border-accent/30 transition-all duration-300 flex justify-between items-center shadow-theme-sm">
                                        <div className="flex-1">
                                            <h3 className="text-lg font-bold uppercase tracking-tight text-theme-text mb-1.5 leading-tight">
                                                {c.title || 'Mega Contest'}
                                            </h3>
                                            <div className="flex items-center gap-3 mt-2">
                                                <div className="flex items-center gap-1.5 bg-theme-bg px-2.5 py-1 rounded-lg border border-theme-border">
                                                    <span className="text-[9px] font-bold text-theme-text-sec uppercase tracking-wider">Best Pts:</span>
                                                    <span className="text-[10px] font-bold text-accent italic">{c.best_points || '0.0'}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 bg-theme-bg px-2.5 py-1 rounded-lg border border-theme-border">
                                                    <span className="text-[9px] font-bold text-theme-text-sec uppercase tracking-wider">Entry:</span>
                                                    <span className="text-[10px] font-bold text-theme-text italic">₹{c.entry_fee}</span>
                                                </div>
                                            </div>

                                            {/* 🔢 JOINED TEAM LABELS (T1, T2) */}
                                            <div className="flex flex-wrap gap-2 mt-4">
                                                {c.joined_team_ids?.map(tid => {
                                                    const idx = myTeams.findIndex(t => t.team_id === tid);
                                                    return (
                                                        <div key={tid} className="bg-accent/10 border border-accent/20 px-2 py-0.5 rounded text-[9px] font-bold text-accent">
                                                            Team {idx !== -1 ? idx + 1 : 'X'}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div className="flex flex-col items-center gap-2">
                                            <div className="px-4 py-1.5 rounded-lg font-bold text-[10px] uppercase tracking-wider shadow-sm border bg-accent text-white border-accent">
                                                Rank {c.best_rank || 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {activeTab === "player_contests" && (
                    <div className="space-y-5">
                        {(() => {
                            const isStarted = selectedMatch && (parseMatchTime(selectedMatch) <= new Date() || selectedMatch.status?.toLowerCase() === "live" || selectedMatch.status?.toLowerCase() === "completed");
                            if (isStarted) return null;
                            return (
                                <button
                                    onClick={() => navigate(`/player-performance?matchId=${selectedMatch.match_id}`)}
                                    className="w-full bg-accent/10 border border-dashed border-accent/30 rounded-xl py-4 flex items-center justify-center gap-3 active:scale-[0.98] transition-all group mb-2"
                                >
                                    <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-white text-lg font-bold shadow-lg shadow-accent/20 group-hover:scale-110 transition-transform">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                    </div>
                                    <span className="text-[10px] font-black uppercase tracking-[0.15em] text-accent">Join New Player Contest</span>
                                </button>
                            );
                        })()}
                        {myChallenges.length === 0 ? (
                            <div className="text-center py-24 bg-theme-card rounded-xl border border-theme-border">
                                <div className="text-4xl mb-4 opacity-30">✨</div>
                                <p className="text-theme-text-sec font-bold text-[10px] uppercase tracking-widest">No player challenges joined</p>
                            </div>
                        ) : (
                            myChallenges.map(e => (
                                <div
                                    key={e.id}
                                    onClick={() => navigate(`/player-leaderboard/${e.challenge_id}`)}
                                    className="relative group cursor-pointer"
                                >
                                    <div className="relative bg-theme-card border border-theme-border rounded-xl p-5 hover:border-accent/30 transition-all duration-300 flex justify-between items-center shadow-theme-sm overflow-hidden">
                                        <div className="flex items-center gap-4 flex-1">
                                            <div className="flex flex-col items-center gap-1.5">
                                                <span className="text-[8px] font-bold text-accent uppercase tracking-wider">{e.category}</span>
                                                <div className="w-12 h-12 rounded-xl overflow-hidden border border-theme-border shadow-sm bg-theme-bg">
                                                    <img src={getAssetURL(e.player_image) || "/player_placeholder.png"} className="w-full h-full object-cover" alt="" />
                                                </div>
                                            </div>

                                            <div className="flex-1">
                                                <h4 className="text-base font-bold text-theme-text uppercase tracking-tight flex items-center gap-2">
                                                    <span className="truncate max-w-[140px]">{e.player_name}</span>
                                                    {/* Edit icon button for upcoming matches */}
                                                    {(() => {
                                                        const isStarted = selectedMatch && (parseMatchTime(selectedMatch) <= new Date() || selectedMatch.status?.toLowerCase() === "live" || selectedMatch.status?.toLowerCase() === "completed");
                                                        if (isStarted) return null;
                                                        return (
                                                            <button
                                                                onClick={(ev) => handleOpenEditModal(e, ev)}
                                                                className="w-7 h-7 bg-accent/10 rounded-md border border-accent/20 flex items-center justify-center shrink-0 active:scale-90 transition-all"
                                                            >
                                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-accent">
                                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                                </svg>
                                                            </button>
                                                        );
                                                    })()}
                                                </h4>
                                                
                                                <div className="text-[9px] font-bold text-theme-text-sec uppercase tracking-widest mt-1 opacity-80 truncate max-w-[180px]">
                                                    {e.title || 'Contest'} <span className="text-accent italic mx-1">•</span> ₹{e.entry_fee || 0} Entry
                                                </div>

                                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                                    <div className="flex items-center gap-1 bg-theme-bg py-0.5 px-2 rounded-lg border border-theme-border">
                                                        <span className="text-[9px] font-bold text-theme-text">{e.prediction}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1 bg-accent/5 py-0.5 px-2 rounded-lg border border-accent/10">
                                                        <span className="text-[9px] font-bold text-accent uppercase italic">
                                                            {e.actual_value || '0'} {e.category}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-col items-center gap-2">
                                            <div className="px-4 py-1.5 rounded-lg font-bold text-[10px] uppercase tracking-wider shadow-sm border bg-accent text-white border-accent">
                                                Rank #{e.current_rank || 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* 🏆 SQUAD PREVIEW MODAL */}
            {selectedTeamForModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-theme-bg/90 backdrop-blur-sm"
                        onClick={() => setSelectedTeamForModal(null)}
                    ></div>

                    <div className="relative bg-theme-card w-full max-w-sm rounded-[40px] border border-white/10 overflow-hidden shadow-2xl animate-in zoom-in duration-300">
                        <div className="p-6 border-b border-white/10 flex justify-between items-center bg-theme-bg/40">
                            <div>
                                <h4 className="text-xl font-black italic text-theme-text uppercase tracking-wider">TEAM PREVIEW</h4>
                                <p className="text-[9px] font-black text-theme-text-sec uppercase tracking-[0.3em]">Total Points: {calculateTeamPoints(selectedTeamForModal.players || [])}</p>
                            </div>
                            <button
                                onClick={() => setSelectedTeamForModal(null)}
                                className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center border border-white/10 text-theme-text hover:bg-white/10 transition-all font-bold"
                            >
                                ×
                            </button>
                        </div>

                        <div className="relative h-[480px] bg-gradient-to-b from-[#064e3b] to-[#01140f] p-4 flex flex-col justify-around">
                            {/* Pitch Visuals */}
                            <div className="absolute inset-x-8 top-12 bottom-12 border border-white/10 rounded-[50%] pointer-events-none transform -skew-y-3"></div>
                            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/pinstriped-suit.png')] opacity-10"></div>

                            {/* Group Players by Roles for Pitch Layout */}
                            {(() => {
                                const players = selectedTeamForModal.players || [];
                                const grouped = {
                                    WK: players.filter(p => (p.role || "").toLowerCase().includes("wk") || (p.role || "").toLowerCase().includes("wick")),
                                    BAT: players.filter(p => (p.role || "").toLowerCase().includes("bat")),
                                    AR: players.filter(p => (p.role || "").toLowerCase().includes("ar") || (p.role || "").toLowerCase().includes("all")),
                                    BOWL: players.filter(p => (p.role || "").toLowerCase().includes("bowl"))
                                };

                                return ["WK", "BAT", "AR", "BOWL"].map(role => (
                                    <div key={role} className="flex justify-center gap-3 relative z-10">
                                        {grouped[role].map(p => (
                                            <div key={p.player_id} className="flex flex-col items-center">
                                                <div className={`w-12 h-12 rounded-full border-2 p-0.5 relative shadow-xl transform transition hover:scale-110 ${p.is_captain ? 'border-yellow-500 shadow-yellow-500/20' : p.is_vice_captain ? 'border-blue-500 shadow-blue-500/20' : 'border-white/20'}`}>
                                                    <div className="w-full h-full rounded-full overflow-hidden bg-theme-bg">
                                                        <img src={getAssetURL(p.image) || "https://img.freepik.com/free-icon/cricket-player_318-80157.jpg"} className="w-full h-full object-cover" alt="" />
                                                    </div>
                                                    {p.is_captain && <span className="absolute -top-1 -right-1 bg-yellow-500 text-black text-[9px] w-5 h-5 rounded-full flex items-center justify-center font-black border border-black z-10">C</span>}
                                                    {p.is_vice_captain && <span className="absolute -top-1 -right-1 bg-blue-500 text-theme-text text-[9px] w-5 h-5 rounded-full flex items-center justify-center font-black border border-black z-10">VC</span>}
                                                </div>
                                                <p className="text-[10px] font-black text-theme-text mt-1 uppercase tracking-tight bg-theme-bg/60 px-1 rounded truncate max-w-[65px]">{p.player_name.split(" ")[0]}</p>
                                                <p className="text-[8px] font-bold text-accent/80 bg-theme-bg/60 px-1.5 rounded-full border border-accent/20">{(p.role || '').toUpperCase() === 'AR' ? 'ALL' : (p.role || '').toUpperCase()}</p>
                                                <p className="text-[9px] font-black text-red-500 italic bg-theme-bg/60 px-1.5 rounded-full border border-red-900/30">
                                                    {((p.points || 0) * (p.is_captain ? 2 : (p.is_vice_captain ? 1.5 : 1))).toFixed(1)} PTS
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                ));
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* ✏️ PLAYER EDIT MODAL */}
            {showPlayerEditModal && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-theme-bg/95 backdrop-blur-md" onClick={() => setShowPlayerEditModal(false)}></div>
                    <div className="relative bg-theme-card w-full max-w-sm rounded-[40px] border border-white/10 overflow-hidden shadow-2xl flex flex-col h-[80vh] animate-in slide-in-from-bottom duration-300">

                        <div className="p-6 border-b border-white/10 flex justify-between items-center bg-theme-bg/40 shrink-0">
                            <div>
                                <h4 className="text-xl font-black italic text-theme-text uppercase tracking-wider">EDIT ENTRY</h4>
                                <p className="text-[9px] font-black text-theme-text-sec uppercase tracking-widest mt-1">Change prediction or swap player</p>
                            </div>
                            <button onClick={() => setShowPlayerEditModal(false)} className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center border border-white/10 text-theme-text font-bold shrink-0">×</button>
                        </div>

                        {/* 🚀 PREDICTION EDIT */}
                        <div className="bg-theme-card p-4 border-b border-white/5 flex items-center justify-between shrink-0">
                            <div>
                                <h5 className="font-bold text-xs uppercase tracking-tight text-theme-text">Prediction</h5>
                                <p className="text-[9px] font-bold text-theme-text-sec uppercase tracking-widest">{editingEntry?.category}</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="flex items-center space-x-1 bg-theme-bg rounded-full border border-theme-border p-1">
                                    <button onClick={() => setEditPrediction(Math.max(0, (Number(editPrediction) || 0) - 1))} className="w-7 h-7 rounded-full bg-theme-card border border-theme-border text-theme-text font-black flex items-center justify-center transition active:scale-95">-</button>
                                    <input type="text" 
                                        inputMode="numeric" 
                                        className="text-sm font-black w-8 bg-transparent text-center text-red-400 outline-none" 
                                        value={editPrediction} 
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/[^0-9]/g, '');
                                            setEditPrediction(val === "" ? "" : Math.max(0, Number(val)));
                                        }} 
                                    />
                                    <button onClick={() => setEditPrediction((Number(editPrediction) || 0) + 1)} className="w-7 h-7 rounded-full bg-theme-card border border-theme-border text-theme-text font-black flex items-center justify-center transition active:scale-95">+</button>
                                </div>
                                <button 
                                    onClick={() => !updatingPlayer && handleUpdatePlayer(editingEntry?.player_id)}
                                    className="bg-accent text-white px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest active:scale-95 shadow-md"
                                >
                                    Save
                                </button>
                            </div>
                        </div>

                        {/* ROLE TABS */}
                        <div className="flex bg-theme-bg/30 p-2 gap-1 border-b border-white/5 overflow-x-auto no-scrollbar">
                            {["EVERYONE", "WK", "BAT", "AR", "BOWL"].map(role => (
                                <button
                                    key={role}
                                    onClick={() => setEditRoleFilter(role)}
                                    className={`px-4 py-2 rounded-full text-[9px] font-black uppercase transition-all whitespace-nowrap ${editRoleFilter === role ? 'bg-red-600 text-theme-text' : 'bg-white/5 text-theme-text-sec hover:text-theme-text'}`}
                                >
                                    {role}
                                </button>
                            ))}
                        </div>

                        {/* PLAYER LIST */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                            {availablePlayers
                                .filter(p => {
                                    if (editRoleFilter === "EVERYONE") return true;
                                    const role = (p.role || "").toLowerCase();
                                    if (editRoleFilter === "WK") return role.includes("wicket") || role === "wk";
                                    if (editRoleFilter === "BAT") return role.includes("bat");
                                    if (editRoleFilter === "AR") return role.includes("all") || role === "ar";
                                    if (editRoleFilter === "BOWL") return role.includes("bowl");
                                    return true;
                                })
                                .map(p => (
                                    <div
                                        key={p.player_id}
                                        onClick={() => !updatingPlayer && handleUpdatePlayer(p.player_id)}
                                        className={`flex items-center justify-between p-4 bg-white/5 rounded-2xl border transition-all active:scale-95 ${p.player_id === editingEntry.player_id ? 'border-red-500 bg-red-500/5' : 'border-white/5 hover:border-white/10'}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-2xl overflow-hidden border border-white/10 relative shadow-lg">
                                                <img src={getAssetURL(p.image) || "https://img.freepik.com/free-icon/cricket-player_318-80157.jpg"} className="w-full h-full object-cover" alt="" />

                                                {/* Playing Dot (Only show if lineup is out) */}
                                                {selectedMatch?.lineup_announced && (
                                                    <div className={`absolute bottom-1 right-1 w-2.5 h-2.5 rounded-full border-2 border-black ${p.is_playing ? 'bg-green-500 shadow-[0_0_5px_rgba(34,197,94,0.5)]' : 'bg-red-500'}`}></div>
                                                )}
                                            </div>
                                            <div>
                                                <h5 className="font-black text-sm text-theme-text uppercase tracking-tight">{p.player_name}</h5>
                                                <p className="text-[9px] font-black text-theme-text-sec uppercase tracking-widest">{p.role} • {p.team_name || 'Team'}</p>
                                            </div>
                                        </div>
                                        {p.player_id === editingEntry.player_id ? (
                                            <span className="text-[8px] font-black text-red-500 uppercase tracking-widest italic bg-red-500/10 px-2 py-1 rounded">Selected</span>
                                        ) : (
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-theme-card border border-theme-border text-theme-text">→</div>
                                        )}
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default MyTeams